import { ModeNames } from "@/modes/mode";
import { selectActiveCadId, setActiveCad } from "@/store/cad/cad-slice";
import { selectModeName } from "@/store/mode-selectors";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import {
  selectShouldUseIntensityData,
  setShouldUseIntensityData,
} from "@/store/modes/walk-mode-slice";
import { SceneFilter } from "@/types/scene-filter";
import { includes } from "@faro-lotv/foundation";
import { isIElementModel3dStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";
import type { splitCompare } from "./split-compare";
import type { TrackedState } from "./tracked-state";

/** The modes showing the scene filter, the ones whose initial state stores it */
const SCENE_FILTER_MODES: readonly ModeNames[] = ["walk", "overview", "split"];

/** The content of the main view, as a subset of a saved view */
export type MainSceneEntry = Pick<SavedViewData, "cadId" | "intensity"> & {
  /** The scene filter of the main view, absent in modes without one */
  scene?: SceneFilter;
};

/**
 * Tracks what the main view shows besides its element: the active CAD, the scene
 * filter and whether panoramas show intensity instead of color. In split mode
 * this is the left side, the right side is tracked by {@link splitCompare}.
 *
 * Restoring sets each recorded field and leaves a missing one alone. The scene
 * filter and intensity are best effort, only a deleted CAD rejects the entry.
 */
export const mainScene: TrackedState<MainSceneEntry> = {
  /**
   * @param state The current state
   * @returns the active CAD if any, the scene filter in modes showing it, and the intensity
   */
  capture(state) {
    const cadId = selectActiveCadId(state);
    return {
      ...(cadId ? { cadId } : {}),
      ...(includes(SCENE_FILTER_MODES, selectModeName(state))
        ? { scene: selectFlyWalkSceneFilter(state) }
        : {}),
      intensity: selectShouldUseIntensityData(state),
    };
  },

  /** @returns whether each field is either missing or well formed */
  isEntry({ cadId, scene, intensity }) {
    return (
      (cadId === undefined || typeof cadId === "string") &&
      (scene === undefined || includes(Object.values(SceneFilter), scene)) &&
      (intensity === undefined || typeof intensity === "boolean")
    );
  },

  /**
   * @param state The current state
   * @returns whether the CAD, if any, still exists and is a CAD
   */
  canRestore({ cadId }, state) {
    if (cadId === undefined) return true;
    const cad = selectIElement(cadId)(state);
    return !!cad && isIElementModel3dStream(cad);
  },

  /** @returns a thunk restoring the recorded fields */
  restore({ cadId, scene, intensity }) {
    return (dispatch) => {
      if (cadId) dispatch(setActiveCad(cadId));
      if (scene) dispatch(setFlyWalkSceneFilter(scene));
      if (intensity !== undefined) {
        dispatch(setShouldUseIntensityData(intensity));
      }
    };
  },
};
